/**
 * Northstar altitude bands for the live trail.
 * Breaks and colors are ours — inspired by public altitude-colored tracks,
 * not a copy of any vendor palette.
 */

export type AltitudeBandId =
  | "ground"
  | "low"
  | "climb"
  | "mid"
  | "high"
  | "upper"
  | "extreme"
  | "unknown";

export type AltitudeBand = {
  id: AltitudeBandId;
  label: string;
  /** Short legend hint. */
  hint: string;
  /** Inclusive lower bound in feet MSL. Unknown has none. */
  minFt: number | null;
  color: string;
};

export const NORTHSTAR_ALTITUDE_BANDS: readonly AltitudeBand[] = [
  { id: "ground", label: "Ground", hint: "under 1,000 ft", minFt: 0, color: "#7D8798" },
  { id: "low", label: "Low", hint: "1,000–4,999 ft", minFt: 1000, color: "#1FA97A" },
  { id: "climb", label: "Climb", hint: "5,000–11,999 ft", minFt: 5000, color: "#2B8CFF" },
  { id: "mid", label: "Mid", hint: "12,000–23,999 ft", minFt: 12000, color: "#E6B325" },
  { id: "high", label: "High", hint: "24,000–33,999 ft", minFt: 24000, color: "#F26B2A" },
  { id: "upper", label: "Upper", hint: "34,000–44,999 ft", minFt: 34000, color: "#A855F7" },
  { id: "extreme", label: "Extreme", hint: "45,000 ft and above", minFt: 45000, color: "#E11D48" },
  { id: "unknown", label: "No alt", hint: "altitude not in the sample", minFt: null, color: "#5C6B7A" },
] as const;

const BY_ID = new Map(NORTHSTAR_ALTITUDE_BANDS.map((band) => [band.id, band]));

export function altitudeBandById(id: string | null | undefined): AltitudeBand {
  return BY_ID.get(id as AltitudeBandId) ?? BY_ID.get("unknown")!;
}

/** Feet MSL → band. Missing / non-finite altitude is unknown — never invented. */
export function altitudeBandForFt(altFt: number | null | undefined): AltitudeBand {
  if (altFt == null || !Number.isFinite(altFt)) return altitudeBandById("unknown");
  const ft = altFt;
  if (ft >= 45000) return altitudeBandById("extreme");
  if (ft >= 34000) return altitudeBandById("upper");
  if (ft >= 24000) return altitudeBandById("high");
  if (ft >= 12000) return altitudeBandById("mid");
  if (ft >= 5000) return altitudeBandById("climb");
  if (ft >= 1000) return altitudeBandById("low");
  return altitudeBandById("ground");
}

export function altitudeLegendHint(): string {
  return "Northstar altitude bands (not a vendor palette). Ground under 1,000 ft through Extreme at 45,000 ft. No-alt stays slate.";
}

export type AltitudeTrackPoint = {
  lat: number;
  lon: number;
  altFt?: number | null;
};

export type AltitudeTrackSegment<T extends AltitudeTrackPoint = AltitudeTrackPoint> = {
  band: AltitudeBandId;
  color: string;
  points: T[];
};

function bandOfPoint(point: AltitudeTrackPoint): AltitudeBand {
  return altitudeBandForFt(point.altFt);
}

/**
 * Continuous pieces colored by the arriving sample’s altitude.
 * Shared vertices keep the line from breaking at a band change.
 */
export function splitAltitudeTrack<T extends AltitudeTrackPoint>(points: readonly T[]): AltitudeTrackSegment<T>[] {
  if (points.length < 2) return [];
  const segments: AltitudeTrackSegment<T>[] = [];
  let current: AltitudeTrackSegment<T> | null = null;
  for (let i = 1; i < points.length; i += 1) {
    const end = points[i]!;
    const start = points[i - 1]!;
    const band = bandOfPoint(end);
    if (!current || current.band !== band.id) {
      current = { band: band.id, color: band.color, points: [start, end] };
      segments.push(current);
    } else {
      current.points.push(end);
    }
  }
  return segments;
}

/** Mapbox `line-color` match. Fallback is the unknown slate so a missing band still draws. */
export function altitudeTrackLineColor(): unknown[] {
  const expr: unknown[] = ["match", ["get", "band"]];
  for (const band of NORTHSTAR_ALTITUDE_BANDS) {
    if (band.id === "unknown") continue;
    expr.push(band.id, band.color);
  }
  expr.push(altitudeBandById("unknown").color);
  return expr;
}

/** Pieces per sample-to-sample leg. Enough steps to read as a blend, not a new schema. */
export const ALTITUDE_GRADIENT_STEPS = 4;

export type AltitudeGradientPiece<T extends AltitudeTrackPoint = AltitudeTrackPoint> = {
  color: string;
  points: T[];
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(rgb: [number, number, number]): string {
  return `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")).join("")}`;
}

function mixHex(a: string, b: string, t: number): string {
  const left = hexToRgb(a);
  const right = hexToRgb(b);
  const k = Math.min(1, Math.max(0, t));
  return rgbToHex([
    left[0] + (right[0] - left[0]) * k,
    left[1] + (right[1] - left[1]) * k,
    left[2] + (right[2] - left[2]) * k,
  ]);
}

const ALTITUDE_STOPS = NORTHSTAR_ALTITUDE_BANDS.filter(
  (band): band is AltitudeBand & { minFt: number } => band.minFt != null,
);

/**
 * Continuous color along the altitude scale. Missing altitude stays the
 * unknown slate — it is not turned into a flight level.
 */
export function altitudeColorForFt(altFt: number | null | undefined): string {
  if (altFt == null || !Number.isFinite(altFt)) return altitudeBandById("unknown").color;
  const first = ALTITUDE_STOPS[0];
  const last = ALTITUDE_STOPS[ALTITUDE_STOPS.length - 1];
  if (!first || !last) return altitudeBandById("unknown").color;
  if (altFt <= first.minFt) return first.color;
  if (altFt >= last.minFt) return last.color;
  for (let i = 1; i < ALTITUDE_STOPS.length; i += 1) {
    const prev = ALTITUDE_STOPS[i - 1]!;
    const next = ALTITUDE_STOPS[i]!;
    if (altFt <= next.minFt) {
      const span = next.minFt - prev.minFt;
      const t = span <= 0 ? 0 : (altFt - prev.minFt) / span;
      return mixHex(prev.color, next.color, t);
    }
  }
  return last.color;
}

function lerpAlt(a: number | null | undefined, b: number | null | undefined, t: number): number | null {
  if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  return a + (b - a) * t;
}

function pointAt<T extends AltitudeTrackPoint>(start: T, end: T, t: number): T {
  return {
    ...start,
    lat: start.lat + (end.lat - start.lat) * t,
    lon: start.lon + (end.lon - start.lon) * t,
    altFt: lerpAlt(start.altFt, end.altFt, t),
  };
}

/**
 * Short pieces whose color is the altitude at the middle of that piece.
 * Shared endpoints keep the line continuous. A climb reads as a gradient
 * instead of one flat band between samples.
 */
export function altitudeGradientTrack<T extends AltitudeTrackPoint>(
  points: readonly T[],
  steps = ALTITUDE_GRADIENT_STEPS,
): AltitudeGradientPiece<T>[] {
  if (points.length < 2) return [];
  const n = Math.max(1, Math.floor(steps));
  const pieces: AltitudeGradientPiece<T>[] = [];
  for (let i = 1; i < points.length; i += 1) {
    const start = points[i - 1]!;
    const end = points[i]!;
    for (let s = 0; s < n; s += 1) {
      const t0 = s / n;
      const t1 = (s + 1) / n;
      const from = t0 === 0 ? start : pointAt(start, end, t0);
      const to = t1 === 1 ? end : pointAt(start, end, t1);
      const mid = lerpAlt(start.altFt, end.altFt, (t0 + t1) / 2);
      pieces.push({ color: altitudeColorForFt(mid), points: [from, to] });
    }
  }
  return pieces;
}

/** Data-driven line color. A piece without `color` still draws in the unknown slate. */
export function altitudeGradientLineColor(): unknown[] {
  return ["case", ["has", "color"], ["get", "color"], altitudeBandById("unknown").color];
}
